import QRCode from "qrcode";
import { getReferralPosterCanvasUrls } from "utils/referralPosterUrl";

/** Canvas 可安全导出的底图缓存（fetch→blob 加载，避免跨域污染） */
const canvasSafeBgCache = new Map();
let canvasPreloadPromise = null;

const POSTER_W = 310;
const POSTER_H = 420;
/** 导出倍率：3x ≈ 930×1260，比 2x 更清晰 */
const EXPORT_SCALE = 3;

const COLOR = {
  white: "#ffffff",
  highlight: "#b9f78a",
  scanTip: "#666666",
  code: "#000000",
};

/** Figma 15225:51659 持仓/交易对海报左上角用户区 */
const POSTER_POSITION_INSET = 16;
const POSTER_AVATAR_SIZE = 24;
const POSTER_AVATAR_GAP = 8;

const POSTER_AVATAR_SVG_DATA_URL = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><g clip-path="url(#clip0)"><path d="M12.0002 0.382141C5.58376 0.382141 0.382324 5.58358 0.382324 12C0.382324 18.4165 5.58376 23.6179 12.0002 23.6179C18.4166 23.6179 23.6181 18.4165 23.6181 12C23.6181 5.58358 18.4166 0.382141 12.0002 0.382141ZM12.0004 20.0576C7.51182 20.0576 5.62914 17.4341 5.62914 17.4341L10.8759 14.8108C10.8759 14.8108 7.87782 12.6 7.87782 9.1892C7.87782 4.42145 10.7321 3.94239 12.0002 3.94239C13.2683 3.94239 16.1228 4.29358 16.1228 9.1892C16.1228 12.8878 13.1246 14.8108 13.1246 14.8108L18.3714 17.809C18.3714 17.809 16.4889 20.0576 12.0004 20.0576Z" fill="white" fill-opacity="0.32"/></g><defs><clipPath id="clip0"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>'
)}`;

let posterAvatarImagePromise = null;

function loadPosterAvatarImage() {
  if (!posterAvatarImagePromise) {
    posterAvatarImagePromise = loadImageElement(POSTER_AVATAR_SVG_DATA_URL).catch(() => null);
  }
  return posterAvatarImagePromise;
}

/**
 * 绘制左上角用户头像 + 昵称 + 时间，返回正文起始 y
 */
async function drawPosterUserHeader(ctx, { userName, shareTime, isRtl }) {
  const contentInset = POSTER_POSITION_INSET;
  const contentW = POSTER_W - contentInset * 2;
  const avatarX = isRtl ? POSTER_W - contentInset - POSTER_AVATAR_SIZE : contentInset;
  const userTextX = isRtl ? contentInset : contentInset + POSTER_AVATAR_SIZE + POSTER_AVATAR_GAP;
  const userTextW = contentW - POSTER_AVATAR_SIZE - POSTER_AVATAR_GAP;
  let y = contentInset;

  const avatarImg = await loadPosterAvatarImage();
  if (avatarImg) {
    ctx.drawImage(avatarImg, avatarX, y, POSTER_AVATAR_SIZE, POSTER_AVATAR_SIZE);
  }

  y += drawPlainText(ctx, userName, userTextX, y, userTextW, {
    fontSize: 14,
    lineHeight: 18,
    color: COLOR.white,
    fontWeight: 500,
    isRtl,
  });
  drawPlainText(ctx, shareTime, userTextX, y, userTextW, {
    fontSize: 11,
    lineHeight: 16,
    color: COLOR.white,
    fontWeight: 400,
    isRtl,
  });

  return contentInset + POSTER_AVATAR_SIZE + 12;
}

/** formatMessage highlight 占位，避免与文案冲突 */
const HL_START = "\uE000";
const HL_END = "\uE001";

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (typeof window !== "undefined") {
    return `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
  }
  return url;
}

function loadImageFromSrc(src, { crossOrigin } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => {
      if (img.naturalWidth > 0) resolve(img);
      else reject(new Error(`empty image: ${src}`));
    };
    img.onerror = () => reject(new Error(`image load failed: ${src}`));
    img.src = src;
  });
}

function isSameOriginUrl(absoluteUrl) {
  if (typeof window === "undefined" || !absoluteUrl) return false;
  if (absoluteUrl.startsWith(window.location.origin)) return true;
  try {
    return new URL(absoluteUrl).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** 优先 fetch→blob（同源或带 CORS 的 CDN），避免 canvas 被跨域图污染导致 toDataURL 失败 */
async function loadImageElement(url) {
  const src = toAbsoluteUrl(url);
  if (!src) throw new Error("image url empty");

  if (src.startsWith("data:")) {
    return loadImageFromSrc(src);
  }

  if (typeof fetch !== "undefined") {
    const sameOrigin = isSameOriginUrl(src);
    if (sameOrigin || /^https?:\/\//i.test(src)) {
      try {
        const resp = await fetch(src, {
          mode: sameOrigin ? "same-origin" : "cors",
          credentials: "omit",
          cache: "force-cache",
        });
        if (resp.ok) {
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          try {
            return await loadImageFromSrc(blobUrl);
          } finally {
            URL.revokeObjectURL(blobUrl);
          }
        }
      } catch {
        /* fall through to Image */
      }
    }
  }

  return loadImageFromSrc(src, { crossOrigin: "anonymous" });
}

function exportCanvasToPng(canvas) {
  try {
    return canvas.toDataURL("image/png");
  } catch (err) {
    throw new Error(`canvas export failed: ${err?.message || err}`);
  }
}

async function loadPosterBackground(variant) {
  const bgVariant = variant === "aicopyOrders" ? "aicopy" : variant;
  const cached = canvasSafeBgCache.get(bgVariant);
  if (cached) return cached;

  const urls = getReferralPosterCanvasUrls(variant);
  let lastErr;
  for (const url of urls) {
    try {
      const img = await loadImageElement(url);
      canvasSafeBgCache.set(bgVariant, img);
      return img;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("poster background load failed");
}

/**
 * 预加载 Canvas 可安全导出的海报底图（fetch→blob，勿与展示用 Image 缓存混用）
 * @returns {Promise<void>}
 */
export function preloadCanvasSafePosterImages() {
  if (typeof window === "undefined") return Promise.resolve();
  if (canvasPreloadPromise) return canvasPreloadPromise;

  canvasPreloadPromise = Promise.all(
    ["refer", "ai", "aicopy", "position"].map((variant) => loadPosterBackground(variant).catch(() => null))
  ).then(() => {});

  return canvasPreloadPromise;
}

function wrapHighlight(formatMessage, messageId) {
  return formatMessage(
    { id: messageId },
    {
      highlight: (chunks) => `${HL_START}${Array.isArray(chunks) ? chunks.join("") : chunks}${HL_END}`,
    }
  );
}

/** @returns {{ text: string, highlight: boolean }[]} */
function parseHighlightSegments(raw) {
  const segments = [];
  const str = String(raw ?? "");
  let cursor = 0;
  while (cursor < str.length) {
    const start = str.indexOf(HL_START, cursor);
    if (start === -1) {
      if (cursor < str.length) segments.push({ text: str.slice(cursor), highlight: false });
      break;
    }
    if (start > cursor) segments.push({ text: str.slice(cursor, start), highlight: false });
    const end = str.indexOf(HL_END, start + HL_START.length);
    if (end === -1) {
      segments.push({ text: str.slice(start), highlight: false });
      break;
    }
    segments.push({ text: str.slice(start + HL_START.length, end), highlight: true });
    cursor = end + HL_END.length;
  }
  return segments.filter((s) => s.text);
}

function measureToken(ctx, token, fontSize, fontWeight) {
  ctx.font = `${fontWeight} ${fontSize}px "SF Pro", "PingFang SC", sans-serif`;
  return ctx.measureText(token.ch).width;
}

function lineWidth(ctx, line, fontSize, fontWeight) {
  return line.reduce((w, tok) => w + measureToken(ctx, tok, fontSize, fontWeight), 0);
}

/** 富文本自动换行，返回绘制过的总高度 */
function drawRichParagraph(ctx, segments, x, y, maxWidth, lineHeight, fontSize, fontWeight, isRtl) {
  const tokens = [];
  segments.forEach((seg) => {
    Array.from(seg.text).forEach((ch) => tokens.push({ ch, highlight: seg.highlight }));
  });

  const lines = [];
  let line = [];
  tokens.forEach((tok) => {
    const test = [...line, tok];
    if (line.length && lineWidth(ctx, test, fontSize, fontWeight) > maxWidth) {
      lines.push(line);
      line = [tok];
    } else {
      line = test;
    }
  });
  if (line.length) lines.push(line);

  let drawY = y;
  lines.forEach((row) => {
    const lw = lineWidth(ctx, row, fontSize, fontWeight);
    let drawX = isRtl ? x + maxWidth - lw : x;
    row.forEach((tok) => {
      ctx.font = `${fontWeight} ${fontSize}px "SF Pro", "PingFang SC", sans-serif`;
      ctx.fillStyle = tok.highlight ? COLOR.highlight : COLOR.white;
      ctx.textBaseline = "top";
      ctx.fillText(tok.ch, drawX, drawY);
      drawX += measureToken(ctx, tok, fontSize, fontWeight);
    });
    drawY += lineHeight;
  });

  return drawY - y;
}

function drawPlainText(ctx, text, x, y, maxWidth, options) {
  const { fontSize, lineHeight, color, fontWeight = 500, isRtl = false } = options;
  ctx.font = `${fontWeight} ${fontSize}px "SF Pro", "PingFang SC", sans-serif`;
  ctx.fillStyle = color;
  ctx.textBaseline = "top";

  const words = String(text).split(/\s+/);
  const lines = [];
  let line = "";

  words.forEach((word, i) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = word;
    }
    if (i === words.length - 1 && line) lines.push(line);
  });
  if (!lines.length && text) lines.push(text);

  let drawY = y;
  lines.forEach((ln) => {
    const w = ctx.measureText(ln).width;
    const drawX = isRtl ? x + maxWidth - w : x;
    ctx.fillText(ln, drawX, drawY);
    drawY += lineHeight;
  });
  return drawY - y;
}

/**
 * 纯 Canvas 合成海报（不修改 DOM，避免闪烁；不依赖 html2canvas）。
 */
export async function captureSharePoster({
  variant,
  formatMessage,
  t,
  inviteCode,
  shareLink,
  isCompact,
  isRtl,
  bgImageUrl,
  posterData,
}) {
  if (typeof window === "undefined") {
    throw new Error("captureSharePoster: SSR");
  }

  const bgImage = bgImageUrl ? await loadImageElement(bgImageUrl) : await loadPosterBackground(variant);
  const qrSize = 50;
  const qrDataUrl = await QRCode.toDataURL(shareLink || " ", {
    width: qrSize * EXPORT_SCALE * 2,
    margin: 0,
    color: { dark: "#000000", light: "#ffffff" },
  });
  const qrImage = await loadImageElement(qrDataUrl);

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const canvas = document.createElement("canvas");
  canvas.width = POSTER_W * EXPORT_SCALE;
  canvas.height = POSTER_H * EXPORT_SCALE;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.scale(EXPORT_SCALE, EXPORT_SCALE);

  ctx.drawImage(bgImage, 0, 0, POSTER_W, POSTER_H);

  const headlineX = isRtl ? POSTER_W - 15 - 276 : 15;
  const headlineW = 276;
  const headlineSize = isCompact ? 13 : 16;
  const headlineLineHeight = isCompact ? 17 : 20;

  if (variant === "aicopy" && posterData) {
    const { strategyName = "", returnRate = "0%", aum = "0" } = posterData;
    const followingText = formatMessage({ id: "aicopy.sharePosterFollowing" }, { strategyName: strategyName || "—" });
    const contentX = isRtl ? POSTER_W - 14 - 277 : 14;
    const contentW = 277;
    let y = 55;

    y += drawPlainText(ctx, followingText, contentX, y, contentW, {
      fontSize: 16,
      lineHeight: 20,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
    y += 20;

    const rateLabel = formatMessage({ id: "aicopy.returnRatePast90Days" });
    y += drawPlainText(ctx, rateLabel, contentX, y, contentW, {
      fontSize: 14,
      lineHeight: 18,
      color: COLOR.white,
      fontWeight: 400,
      isRtl,
    });
    drawPlainText(ctx, returnRate, contentX, y, contentW, {
      fontSize: 40,
      lineHeight: 44,
      color: COLOR.highlight,
      fontWeight: 700,
      isRtl,
    });

    const aumX = isRtl ? POSTER_W - 16 - 277 : 16;
    const aumLabel = t("aicopy.assetsUnderManagement");
    let aumY = 259;
    aumY += drawPlainText(ctx, aumLabel, aumX, aumY, contentW, {
      fontSize: 14,
      lineHeight: 18,
      color: COLOR.white,
      fontWeight: 400,
      isRtl,
    });
    aumY += 6;
    drawPlainText(ctx, aum, aumX, aumY, contentW, {
      fontSize: 20,
      lineHeight: 24,
      color: COLOR.highlight,
      fontWeight: 700,
      isRtl,
    });
  } else if (variant === "position" && posterData) {
    const {
      userName = "",
      shareTime = "",
      pair = "",
      productName = "",
      sideText = "",
      leverage = "",
      pnlValue = "0",
      pnlUnit = "USDT",
      isNegative = false,
      entryPrice = "—",
      latestPrice = "—",
    } = posterData;
    const contentW = POSTER_W - POSTER_POSITION_INSET * 2;
    const contentX = isRtl ? POSTER_W - POSTER_POSITION_INSET - contentW : POSTER_POSITION_INSET;
    let y = await drawPosterUserHeader(ctx, { userName, shareTime, isRtl });

    const pairLine = productName ? `${pair}  ${productName}` : pair;
    y += drawPlainText(ctx, pairLine, contentX, y, contentW, {
      fontSize: 14,
      lineHeight: 20,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
    y += drawPlainText(ctx, `${sideText} | ${leverage}`, contentX, y, contentW, {
      fontSize: 12,
      lineHeight: 18,
      color: "rgba(255,255,255,0.32)",
      fontWeight: 400,
      isRtl,
    });
    y += 8;

    drawPlainText(ctx, `${pnlValue} ${pnlUnit}`.trim(), contentX, y, contentW, {
      fontSize: 28,
      lineHeight: 36,
      color: isNegative ? "#d94f4f" : COLOR.highlight,
      fontWeight: 500,
      isRtl,
    });
    y += 40;

    const entryLabel = t("cfd.entryPrice");
    const latestLabel = t("cfd.latestPrice");
    drawPlainText(ctx, entryLabel, contentX, y, contentW / 2, {
      fontSize: 11,
      lineHeight: 16,
      color: "rgba(255,255,255,0.56)",
      fontWeight: 400,
      isRtl,
    });
    drawPlainText(ctx, latestLabel, contentX + contentW / 2, y, contentW / 2, {
      fontSize: 11,
      lineHeight: 16,
      color: "rgba(255,255,255,0.56)",
      fontWeight: 400,
      isRtl,
    });
    y += 16;
    drawPlainText(ctx, entryPrice, contentX, y, contentW / 2, {
      fontSize: 11,
      lineHeight: 16,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
    drawPlainText(ctx, latestPrice, contentX + contentW / 2, y, contentW / 2, {
      fontSize: 11,
      lineHeight: 16,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
  } else if (variant === "pair" && posterData) {
    const {
      userName = "",
      shareTime = "",
      pair = "",
      productName = "",
      latestPrice = "—",
    } = posterData;
    const contentW = POSTER_W - POSTER_POSITION_INSET * 2;
    const contentX = isRtl ? POSTER_W - POSTER_POSITION_INSET - contentW : POSTER_POSITION_INSET;
    let y = await drawPosterUserHeader(ctx, { userName, shareTime, isRtl });

    const pairLine = productName ? `${pair}  ${productName}` : pair;
    y += drawPlainText(ctx, pairLine, contentX, y, contentW, {
      fontSize: 14,
      lineHeight: 20,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
    y += 24;

    const latestLabel = t("cfd.latestPrice");
    y += drawPlainText(ctx, latestLabel, contentX, y, contentW, {
      fontSize: 11,
      lineHeight: 16,
      color: "rgba(255,255,255,0.56)",
      fontWeight: 400,
      isRtl,
    });
    y += 4;
    drawPlainText(ctx, latestPrice, contentX, y, contentW, {
      fontSize: 28,
      lineHeight: 36,
      color: COLOR.highlight,
      fontWeight: 500,
      isRtl,
    });
  } else if (variant === "aicopyOrders" && posterData) {
    const orders = Array.isArray(posterData.orders) ? posterData.orders : [];
    const contentX = isRtl ? POSTER_W - 14 - 277 : 14;
    const contentW = 277;
    let y = 55;

    y += drawPlainText(ctx, t("aicopy.currentOrders"), contentX, y, contentW, {
      fontSize: 16,
      lineHeight: 20,
      color: COLOR.white,
      fontWeight: 500,
      isRtl,
    });
    y += 8;

    orders.slice(0, 2).forEach((order) => {
      y += drawPlainText(ctx, order.name || "—", contentX, y, contentW * 0.65, {
        fontSize: 14,
        lineHeight: 18,
        color: COLOR.white,
        fontWeight: 500,
        isRtl,
      });
      drawPlainText(ctx, order.returnRate || "0%", contentX + contentW * 0.65, y - 18, contentW * 0.35, {
        fontSize: 16,
        lineHeight: 18,
        color: COLOR.highlight,
        fontWeight: 700,
        isRtl,
      });
      y += 6;
    });
  } else if (variant === "ai") {
    const top = isCompact ? 46 : 46;
    let y = top;
    y += drawRichParagraph(
      ctx,
      parseHighlightSegments(wrapHighlight(formatMessage, "home.sharePosterAiHeadline1")),
      headlineX,
      y,
      headlineW,
      headlineLineHeight,
      headlineSize,
      500,
      isRtl
    );
    y += drawRichParagraph(
      ctx,
      parseHighlightSegments(formatMessage({ id: "home.sharePosterAiHeadline2" })),
      headlineX,
      y,
      headlineW,
      headlineLineHeight,
      headlineSize,
      500,
      isRtl
    );
    y += 3;
    drawRichParagraph(
      ctx,
      parseHighlightSegments(wrapHighlight(formatMessage, "home.sharePosterAiHeadlineSub")),
      headlineX,
      y,
      headlineW,
      isCompact ? 15 : 16,
      isCompact ? 11 : 12,
      500,
      isRtl
    );
  } else {
    drawRichParagraph(
      ctx,
      parseHighlightSegments(wrapHighlight(formatMessage, "home.sharePosterReferHeadline")),
      headlineX,
      48,
      headlineW,
      headlineLineHeight,
      headlineSize,
      500,
      isRtl
    );
  }

  const footerX = isRtl ? POSTER_W - 12 - 236 : 12;
  const footerW = 236;
  drawPlainText(ctx, t("home.sharePosterScanRegister"), footerX, 366, footerW, {
    fontSize: 12,
    lineHeight: 16,
    color: COLOR.scanTip,
    isRtl,
  });
  const codeLabel = t("home.sharePosterCodeLabel");
  const codeText = `${codeLabel}${inviteCode ? ` ${inviteCode}` : " —"}`;
  const codeColor = variant === "aicopy" || variant === "position" || variant === "pair" || variant === "aicopyOrders" ? COLOR.white : COLOR.code;
  drawPlainText(ctx, codeText, footerX, 384, footerW, {
    fontSize: 18,
    lineHeight: 20,
    color: codeColor,
    isRtl,
  });

  const qrInset = 12;
  const qrX = isRtl ? qrInset : POSTER_W - qrInset - qrSize;
  const qrY = POSTER_H - qrInset - qrSize;
  ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  return exportCanvasToPng(canvas);
}
